import { useEffect, useState } from 'react';
import { adminApi } from '../../services/admin/admin.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ResultsEditor() {
    const [levels, setLevels] = useState<any[]>([]);
    const [resultsText, setResultsText] = useState<any>(null);
    const [insights, setInsights] = useState<any[]>([]);
    const [ranges, setRanges] = useState<any[]>([]);
    const [catInsights, setCatInsights] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            adminApi.getLevels().then(setLevels),
            adminApi.getResultsText().then(setResultsText),
            adminApi.getLevelInsights().then(setInsights),
            adminApi.getLevelRanges().then(setRanges),
            adminApi.getCategoryInsights().then(setCatInsights),
        ]).catch(e => toast.error('Erro: ' + e.message)).finally(() => setLoading(false));
    }, []);

    // ─── Results Text ───
    const [textForm, setTextForm] = useState<any>({});
    const [savingText, setSavingText] = useState(false);
    useEffect(() => { if (resultsText) setTextForm(resultsText); }, [resultsText]);

    const saveText = async () => {
        setSavingText(true);
        try { await adminApi.updateResultsText(textForm); toast.success('Textos salvos!'); } catch (e: any) { toast.error(e.message); } finally { setSavingText(false); }
    };

    // ─── Levels ───
    const [editLevel, setEditLevel] = useState<number | null>(null);
    const [levelForm, setLevelForm] = useState<any>({});

    const saveLevel = async () => {
        try {
            const updated = await adminApi.updateLevel(levelForm.id, levelForm);
            setLevels(levels.map(l => l.id === levelForm.id ? updated : l));
            toast.success('Nível salvo!');
            setEditLevel(null);
        } catch (e: any) { toast.error(e.message); }
    };

    // ─── Ranges ───
    const [editRange, setEditRange] = useState<number | null>(null);
    const [rangeForm, setRangeForm] = useState<any>({});

    const saveRange = async () => {
        try {
            const updated = await adminApi.updateLevelRange(rangeForm.id, rangeForm);
            setRanges(ranges.map(r => r.id === rangeForm.id ? updated : r));
            toast.success('Faixa salva!');
            setEditRange(null);
        } catch (e: any) { toast.error(e.message); }
    };

    // ─── Insights ───
    const [editInsight, setEditInsight] = useState<number | null>(null);
    const [insightForm, setInsightForm] = useState<any>({});

    const saveInsight = async () => {
        try {
            const result = await adminApi.upsertLevelInsight(insightForm);
            setInsights(insights.map(i => i.id === result.id ? result : i));
            toast.success('Insight salvo!');
            setEditInsight(null);
        } catch (e: any) { toast.error(e.message); }
    };

    // ─── Category Insights ───
    const [editCatInsight, setEditCatInsight] = useState<number | null>(null);
    const [catInsightForm, setCatInsightForm] = useState<any>({});

    const saveCatInsight = async () => {
        try {
            const result = await adminApi.upsertCategoryInsight(catInsightForm);
            setCatInsights(catInsights.map(i => i.id === result.id ? result : i));
            toast.success('Insight de categoria salvo!');
            setEditCatInsight(null);
        } catch (e: any) { toast.error(e.message); }
    };

    if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;

    const textFields = [
        { key: 'hero_start', label: 'Hero Início' },
        { key: 'hero_content', label: 'Hero Conteúdo' },
        { key: 'hero_icon', label: 'Hero Ícone' },
        { key: 'hero_score_text', label: 'Texto do Score' },
        { key: 'insight_title', label: 'Título dos Insights' },
        { key: 'category_title', label: 'Título das Categorias' },
        { key: 'help_text_icon', label: 'Ícone do Help Text' },
        { key: 'help_text_title', label: 'Título do Help Text' },
        { key: 'recommendation', label: 'Recomendação' },
        { key: 'conclusion_title', label: 'Título da Conclusão' },
        { key: 'conclusion_positive_feedback', label: 'Feedback Positivo' },
        { key: 'conclusion_attention_feedback', label: 'Feedback de Atenção' },
        { key: 'conclusion_advice', label: 'Conselho' },
        { key: 'content_title', label: 'Título do Conteúdo' },
        { key: 'content_description', label: 'Descrição do Conteúdo' },
        { key: 'region_title', label: 'Título da Região' },
        { key: 'region_content', label: 'Conteúdo da Região' },
    ];

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/dashboard"><ArrowLeft className="h-5 w-5 text-gray-500 hover:text-gray-800" /></Link>
                <h1 className="text-2xl font-bold">Resultados & Níveis</h1>
            </div>

            <Tabs defaultValue="levels">
                <TabsList className="mb-4 flex-wrap">
                    <TabsTrigger value="levels">Níveis ({levels.length})</TabsTrigger>
                    <TabsTrigger value="ranges">Faixas de Score</TabsTrigger>
                    <TabsTrigger value="insights">Insights por Nível</TabsTrigger>
                    <TabsTrigger value="cat-insights">Insights por Categoria</TabsTrigger>
                    <TabsTrigger value="text">Textos</TabsTrigger>
                </TabsList>

                {/* LEVELS */}
                <TabsContent value="levels">
                    <Card>
                        <CardHeader><CardTitle>Níveis de Maturidade</CardTitle></CardHeader>
                        <CardContent>
                            {levels.map(l => (
                                <div key={l.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2">
                                    {editLevel === l.id ? (
                                        <>
                                            <Input value={levelForm.title || ''} onChange={e => setLevelForm({ ...levelForm, title: e.target.value })} className="flex-1" />
                                            <Button size="sm" onClick={saveLevel}>Salvar</Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditLevel(null)}>Cancelar</Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-sm text-gray-400">#{l.id}</span>
                                            <span className="flex-1 font-medium">{l.title}</span>
                                            <Button size="sm" variant="ghost" onClick={() => { setEditLevel(l.id); setLevelForm(l); }}>Editar</Button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* RANGES */}
                <TabsContent value="ranges">
                    <Card>
                        <CardHeader><CardTitle>Faixas de Score</CardTitle></CardHeader>
                        <CardContent>
                            {ranges.map(r => (
                                <div key={r.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2">
                                    {editRange === r.id ? (
                                        <>
                                            <Input type="number" value={rangeForm.min_score ?? ''} onChange={e => setRangeForm({ ...rangeForm, min_score: Number(e.target.value) })} placeholder="Min" className="w-24" />
                                            <span>—</span>
                                            <Input type="number" value={rangeForm.max_score ?? ''} onChange={e => setRangeForm({ ...rangeForm, max_score: Number(e.target.value) })} placeholder="Max" className="w-24" />
                                            <Button size="sm" onClick={saveRange}>Salvar</Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditRange(null)}>Cancelar</Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-sm text-gray-500">{r.levels?.title || `Nível ${r.level_id}`}:</span>
                                            <span className="font-mono font-medium">{r.min_score} — {r.max_score}</span>
                                            <div className="flex-1" />
                                            <Button size="sm" variant="ghost" onClick={() => { setEditRange(r.id); setRangeForm(r); }}>Editar</Button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* INSIGHTS */}
                <TabsContent value="insights">
                    <Card>
                        <CardHeader><CardTitle>Insights por Nível</CardTitle></CardHeader>
                        <CardContent>
                            {insights.map(i => (
                                <div key={i.id} className="border rounded-lg p-3 mb-2">
                                    {editInsight === i.id ? (
                                        <div className="space-y-2">
                                            <label className="text-xs text-gray-500">Nível: {i.levels?.title || i.level_id}</label>
                                            <Textarea value={insightForm.description || ''} onChange={e => setInsightForm({ ...insightForm, description: e.target.value })} />
                                            <Input value={insightForm.alt_image || ''} onChange={e => setInsightForm({ ...insightForm, alt_image: e.target.value })} placeholder="Alt da imagem" />
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={saveInsight}>Salvar</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditInsight(null)}>Cancelar</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <span className="text-sm font-medium text-blue-600">{i.levels?.title || `Nível ${i.level_id}`}</span>
                                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{i.description}</p>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => { setEditInsight(i.id); setInsightForm(i); }}>Editar</Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* CATEGORY INSIGHTS */}
                <TabsContent value="cat-insights">
                    <Card>
                        <CardHeader><CardTitle>Insights por Categoria</CardTitle></CardHeader>
                        <CardContent>
                            {catInsights.map(ci => (
                                <div key={ci.id} className="border rounded-lg p-3 mb-2">
                                    {editCatInsight === ci.id ? (
                                        <div className="space-y-2">
                                            <div className="text-xs text-gray-500">
                                                {ci.categories?.title || `Cat ${ci.category_id}`} × {ci.levels?.title || `Nível ${ci.level_id}`}
                                            </div>
                                            <Textarea value={catInsightForm.description || ''} onChange={e => setCatInsightForm({ ...catInsightForm, description: e.target.value })} placeholder="Descrição" />
                                            <Textarea value={catInsightForm.support_text || ''} onChange={e => setCatInsightForm({ ...catInsightForm, support_text: e.target.value })} placeholder="Texto de apoio" />
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={saveCatInsight}>Salvar</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditCatInsight(null)}>Cancelar</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex gap-2">
                                                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{ci.categories?.title || `Cat ${ci.category_id}`}</span>
                                                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{ci.levels?.title || `Nível ${ci.level_id}`}</span>
                                                </div>
                                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ci.description}</p>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => { setEditCatInsight(ci.id); setCatInsightForm(ci); }}>Editar</Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* TEXT */}
                <TabsContent value="text">
                    <Card>
                        <CardHeader><CardTitle>Textos da Página de Resultado</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {textFields.map(f => (
                                    <div key={f.key}>
                                        <label className="text-sm font-medium text-gray-600">{f.label}</label>
                                        <Input value={textForm[f.key] || ''} onChange={e => setTextForm({ ...textForm, [f.key]: e.target.value })} className="mt-1" />
                                    </div>
                                ))}
                            </div>
                            <Button onClick={saveText} disabled={savingText} className="mt-4">
                                {savingText ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Salvar Textos
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
